import React from 'react';
import PropTypes from 'prop-types';
import styles from './Tabs.module.scss';

/* ────────────────────────────────────────────────────────────
 * Tabs — pure controlled tab strip primitive.
 *
 *   <Tabs
 *     activeKey={key}
 *     onChange={setKey}
 *     items={[
 *       { key: 'personal',   label: 'Personal Details' },
 *       { key: 'contact',    label: 'Contact Details'  },
 *     ]}
 *   />
 *
 * Only renders the strip — the consumer renders matching
 * content next to / below it based on `activeKey`. This keeps
 * the primitive flexible (works inside a PagePanel, inside a
 * PageToolbar's left slot, or standalone) and lets the consumer
 * own its own layout.
 * ──────────────────────────────────────────────────────────── */

const Tabs = ({ activeKey, onChange, items, className, ariaLabel }) => {
  const stripClass = [styles.tabs, className || ''].filter(Boolean).join(' ');

  return (
    <div role="tablist" aria-label={ariaLabel} className={stripClass}>
      {items.map((item) => {
        const isActive = item.key === activeKey;
        const tabClass = [
          styles.tab,
          isActive ? styles.tabActive : '',
          item.disabled ? styles.tabDisabled : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={item.disabled || undefined}
            disabled={item.disabled}
            className={tabClass}
            onClick={() => !item.disabled && onChange(item.key)}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
};

Tabs.propTypes = {
  activeKey: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

Tabs.defaultProps = {
  className: '',
  ariaLabel: undefined,
};

export default Tabs;
